/**
 * Product Catalog generator.
 *
 * Lets staff filter the product database by brand / category / search +
 * preview the catalog live, then export a branded PDF (image-rich catalog
 * OR compact price list) suitable for sending to customers, distributors
 * and salons.
 *
 * Designed to scale to 2000+ products — uses server-side WeasyPrint via
 * POST /api/catalog/export.pdf instead of `window.print()`.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { brandsAPI, categoriesAPI, catalogAPI } from '../lib/api';
import {
    Search, Filter, Layers, LayoutGrid, List as ListIcon,
    Tag, Download, FileText, ImageIcon, Eye, EyeOff,
} from 'lucide-react';

const placeholderSvg =
    "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><rect width='200' height='200' fill='%23F5EFEC'/><text x='100' y='105' fill='%23C08478' text-anchor='middle' font-family='Arial' font-size='24' font-weight='600'>UC</text></svg>";

const Catalog = () => {
    const { t } = useTranslation(['entities', 'common']);

    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [search, setSearch] = useState('');
    const [selectedBrandIds, setSelectedBrandIds] = useState(new Set());
    const [selectedCategoryId, setSelectedCategoryId] = useState('all');
    const [activeOnly, setActiveOnly] = useState(true);

    const [layout, setLayout] = useState('brand_grouped');   // grid | detailed | brand_grouped
    const [format, setFormat] = useState('catalog');          // catalog | price_list
    const [showPrices, setShowPrices] = useState(true);
    const [showSku, setShowSku] = useState(true);
    const [showDescription, setShowDescription] = useState(false);
    const [showBrand, setShowBrand] = useState(true);
    const [showCategory, setShowCategory] = useState(true);
    const [coverPage, setCoverPage] = useState(false);

    // Load brands + categories once on mount; preselect all brands.
    useEffect(() => {
        (async () => {
            try {
                const [{ data: bs }, { data: cs }] = await Promise.all([brandsAPI.getAll(), categoriesAPI.getAll()]);
                setBrands(bs || []);
                setCategories(cs || []);
                setSelectedBrandIds(new Set((bs || []).map((b) => b.id)));
            } catch (err) {
                toast.error('Failed to load brands/categories');
            }
        })();
    }, []);

    // Re-fetch products whenever filters change (debounce search).
    useEffect(() => {
        const handle = setTimeout(async () => {
            setLoading(true);
            try {
                // Build query params manually — axios's default array
                // serializer (brand_ids[]=A&brand_ids[]=B) is incompatible
                // with FastAPI's repeated-key parsing. URLSearchParams
                // emits brand_ids=A&brand_ids=B which FastAPI accepts.
                const params = new URLSearchParams();
                Array.from(selectedBrandIds).forEach((id) => params.append('brand_ids', id));
                if (selectedCategoryId !== 'all') params.append('category_ids', selectedCategoryId);
                if (search) params.append('search', search);
                params.append('active_only', String(activeOnly));
                const { data } = await catalogAPI.products(params);
                setProducts(data.products || []);
            } catch {
                toast.error('Failed to load products');
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => clearTimeout(handle);
    }, [selectedBrandIds, selectedCategoryId, search, activeOnly]);

    const totalCount = products.length;
    const groupedByBrand = useMemo(() => {
        const m = new Map();
        products.forEach((p) => {
            const key = p.brand || 'Sans marque';
            if (!m.has(key)) m.set(key, []);
            m.get(key).push(p);
        });
        return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [products]);

    const brandLogoByName = useMemo(() => {
        const m = {};
        brands.forEach((b) => { if (b.logo_url) m[b.name] = b.logo_url; });
        return m;
    }, [brands]);

    const toggleBrand = (id) => {
        const next = new Set(selectedBrandIds);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelectedBrandIds(next);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const payload = {
                brand_ids: Array.from(selectedBrandIds),
                category_ids: selectedCategoryId !== 'all' ? [selectedCategoryId] : [],
                search: search || null,
                active_only: activeOnly,
                layout, format,
                show_prices: showPrices,
                show_sku: showSku,
                show_description: showDescription,
                show_brand: showBrand,
                show_category: showCategory,
                cover_page: coverPage,
            };
            const { data } = await catalogAPI.exportPdf(payload);
            const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = format === 'price_list' ? 'liste-prix.pdf' : 'catalogue-produits.pdf';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`PDF généré · ${totalCount} produit${totalCount > 1 ? 's' : ''}`);
        } catch (err) {
            toast.error("Échec de l'export PDF");
            console.error(err);
        } finally {
            setExporting(false);
        }
    };

    const formatMoney = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

    return (
        <div data-testid="catalog-page" className="space-y-5 animate-fadeIn">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">Catalogue produits</h1>
                    <p className="text-gray-500 text-sm mt-1">
                        Générez un catalogue PDF professionnel ou une liste de prix à partager avec vos clients.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Tabs value={format} onValueChange={setFormat}>
                        <TabsList>
                            <TabsTrigger value="catalog" data-testid="format-catalog"><ImageIcon size={14} className="mr-1.5" />Catalogue</TabsTrigger>
                            <TabsTrigger value="price_list" data-testid="format-price-list"><FileText size={14} className="mr-1.5" />Liste de prix</TabsTrigger>
                        </TabsList>
                    </Tabs>
                    <Button
                        onClick={handleExport}
                        disabled={exporting || !totalCount}
                        data-testid="export-catalog-pdf"
                        className="bg-[#1A1A1A] hover:bg-[#C08478] text-white"
                    >
                        <Download size={14} className="mr-2" />
                        {exporting ? 'Génération…' : 'Exporter PDF'}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-12 gap-5">
                {/* Sidebar filters */}
                <Card className="col-span-12 lg:col-span-3 border-0 shadow-sm">
                    <CardContent className="p-4 space-y-5">
                        <div>
                            <Label className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-2">
                                <Search size={12} /> Recherche
                            </Label>
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Nom, SKU…"
                                data-testid="catalog-search"
                                className="mt-1.5"
                            />
                        </div>

                        <div>
                            <Label className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-2">
                                <Tag size={12} /> Catégorie
                            </Label>
                            <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
                                <SelectTrigger className="mt-1.5" data-testid="catalog-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Toutes les catégories</SelectItem>
                                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <Label className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-2">
                                    <Filter size={12} /> Marques ({selectedBrandIds.size}/{brands.length})
                                </Label>
                                <div className="flex gap-1.5 text-xs">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedBrandIds(new Set(brands.map((b) => b.id)))}
                                        data-testid="brands-select-all"
                                        className="text-[#C08478] hover:underline"
                                    >Tout</button>
                                    <span className="text-gray-300">·</span>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedBrandIds(new Set())}
                                        data-testid="brands-deselect-all"
                                        className="text-gray-500 hover:underline"
                                    >Aucun</button>
                                </div>
                            </div>
                            <ScrollArea className="h-[240px] border border-gray-100 rounded-md p-2 bg-white">
                                {brands.map((b) => (
                                    <label
                                        key={b.id}
                                        className="flex items-center gap-2 py-1 px-1.5 rounded hover:bg-[#FBF8F6] cursor-pointer"
                                    >
                                        <Checkbox
                                            checked={selectedBrandIds.has(b.id)}
                                            onCheckedChange={() => toggleBrand(b.id)}
                                            data-testid={`brand-checkbox-${b.prefix}`}
                                        />
                                        {b.logo_url ? (
                                            <img src={b.logo_url} alt="" className="h-5 w-5 object-contain" />
                                        ) : (
                                            <span className="h-5 w-5 inline-flex items-center justify-center bg-gray-100 text-[10px] text-gray-500 rounded">{b.prefix}</span>
                                        )}
                                        <span className="text-sm truncate">{b.name}</span>
                                    </label>
                                ))}
                            </ScrollArea>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                            <Label htmlFor="active-only" className="text-xs cursor-pointer">Produits actifs uniquement</Label>
                            <Switch
                                id="active-only"
                                checked={activeOnly}
                                onCheckedChange={setActiveOnly}
                                data-testid="catalog-active-only"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Main content — settings + preview */}
                <div className="col-span-12 lg:col-span-9 space-y-4">
                    {/* Settings row */}
                    <Card className="border-0 shadow-sm">
                        <CardContent className="p-3 flex flex-wrap items-center gap-3">
                            {format === 'catalog' && (
                                <Tabs value={layout} onValueChange={setLayout}>
                                    <TabsList>
                                        <TabsTrigger value="brand_grouped" data-testid="layout-brand-grouped"><Layers size={13} className="mr-1.5" />Par marque</TabsTrigger>
                                        <TabsTrigger value="grid" data-testid="layout-grid"><LayoutGrid size={13} className="mr-1.5" />Grille</TabsTrigger>
                                        <TabsTrigger value="detailed" data-testid="layout-detailed"><ListIcon size={13} className="mr-1.5" />Détaillée</TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            )}
                            <div className="h-6 w-px bg-gray-200" />
                            <ToggleChip label="Prix" checked={showPrices} onChange={setShowPrices} testId="toggle-price" icon={showPrices ? Eye : EyeOff} />
                            <ToggleChip label="SKU" checked={showSku} onChange={setShowSku} testId="toggle-sku" />
                            <ToggleChip label="Description" checked={showDescription} onChange={setShowDescription} testId="toggle-desc" />
                            <ToggleChip label="Marque" checked={showBrand} onChange={setShowBrand} testId="toggle-brand" />
                            <ToggleChip label="Catégorie" checked={showCategory} onChange={setShowCategory} testId="toggle-cat" />
                            <ToggleChip label="Page de couverture" checked={coverPage} onChange={setCoverPage} testId="toggle-cover" />
                            <span className="ml-auto text-sm text-gray-500" data-testid="catalog-count">
                                {loading ? 'Chargement…' : `${totalCount} produit${totalCount > 1 ? 's' : ''}`}
                            </span>
                        </CardContent>
                    </Card>

                    {/* Preview */}
                    <Card className="border-0 shadow-sm">
                        <CardContent className="p-4">
                            {loading && <div className="py-10 text-center text-gray-400">Chargement du catalogue…</div>}
                            {!loading && totalCount === 0 && (
                                <div className="py-10 text-center text-gray-400">
                                    Aucun produit ne correspond à ces filtres.
                                </div>
                            )}
                            {!loading && totalCount > 0 && (
                                <>
                                    {format === 'price_list' ? (
                                        <PriceListPreview groupedByBrand={groupedByBrand} formatMoney={formatMoney} />
                                    ) : layout === 'detailed' ? (
                                        <DetailedPreview products={products} showSku={showSku} showBrand={showBrand} showCategory={showCategory} showDescription={showDescription} showPrices={showPrices} formatMoney={formatMoney} />
                                    ) : layout === 'grid' ? (
                                        <GridPreview products={products} showSku={showSku} showBrand={showBrand} showCategory={showCategory} showDescription={showDescription} showPrices={showPrices} formatMoney={formatMoney} />
                                    ) : (
                                        <BrandGroupedPreview groupedByBrand={groupedByBrand} brandLogoByName={brandLogoByName} showSku={showSku} showBrand={showBrand} showCategory={showCategory} showDescription={showDescription} showPrices={showPrices} formatMoney={formatMoney} />
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

// -------------------- Sub components -------------------- //
const ToggleChip = ({ label, checked, onChange, testId, icon: Icon = null }) => (
    <label
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs cursor-pointer transition ${checked ? 'bg-[#C08478]/10 text-[#C08478]' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
    >
        <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} className="scale-75 origin-center -my-1" />
        {Icon && <Icon size={12} />}
        {label}
    </label>
);

const ProductCard = ({ p, showSku, showBrand, showCategory, showDescription, showPrices, formatMoney }) => (
    <div className="border border-gray-100 rounded-lg p-2.5 bg-white flex flex-col" data-testid="catalog-card">
        <div className="aspect-square w-full bg-[#FBF8F6] rounded mb-2 overflow-hidden flex items-center justify-center">
            <img
                src={p.image_url || placeholderSvg}
                alt={p.name}
                onError={(e) => { e.target.src = placeholderSvg; }}
                className="max-w-full max-h-full object-contain"
            />
        </div>
        <div className="font-semibold text-sm leading-tight line-clamp-2">{p.name}</div>
        {(showBrand && p.brand) || (showCategory && p.category) ? (
            <div className="text-[11px] text-gray-500 mt-0.5">
                {showBrand && p.brand && <span className="font-medium text-[#C08478]">{p.brand}</span>}
                {showBrand && p.brand && showCategory && p.category && ' · '}
                {showCategory && p.category && <span>{p.category}</span>}
            </div>
        ) : null}
        {showSku && p.sku && <div className="text-[11px] text-gray-400 font-mono mt-0.5">{p.sku}</div>}
        {showDescription && p.description && <div className="text-[11px] text-gray-500 mt-1 line-clamp-2">{p.description}</div>}
        {showPrices && (
            <div className="text-[#C08478] font-bold mt-auto pt-1.5 text-sm">{formatMoney(p.price)}</div>
        )}
    </div>
);

const GridPreview = ({ products, ...opts }) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {products.map((p) => <ProductCard key={p.id} p={p} {...opts} />)}
    </div>
);

const DetailedPreview = ({ products, formatMoney, showSku, showBrand, showCategory, showDescription, showPrices }) => (
    <div className="space-y-2">
        {products.map((p) => (
            <div key={p.id} className="flex gap-3 p-3 border border-gray-100 rounded-lg bg-white">
                <div className="w-20 h-20 flex-none bg-[#FBF8F6] rounded flex items-center justify-center overflow-hidden">
                    <img src={p.image_url || placeholderSvg} alt={p.name} onError={(e) => { e.target.src = placeholderSvg; }} className="max-w-full max-h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                        {showBrand && p.brand && <span className="font-medium text-[#C08478]">{p.brand}</span>}
                        {showBrand && p.brand && showCategory && p.category && ' · '}
                        {showCategory && p.category && <span>{p.category}</span>}
                        {showSku && p.sku && <span> · <span className="font-mono">{p.sku}</span></span>}
                    </div>
                    {showDescription && p.description && <div className="text-xs text-gray-600 mt-1.5">{p.description}</div>}
                </div>
                {showPrices && <div className="self-center text-[#C08478] font-bold text-lg">{formatMoney(p.price)}</div>}
            </div>
        ))}
    </div>
);

const BrandGroupedPreview = ({ groupedByBrand, brandLogoByName, ...opts }) => (
    <div className="space-y-5">
        {groupedByBrand.map(([brandName, items]) => (
            <div key={brandName}>
                <div className="bg-gradient-to-r from-[#1A1A1A] to-[#C08478] text-white px-3 py-2 rounded flex items-center gap-2.5 mb-2.5">
                    {brandLogoByName[brandName] && (
                        <img src={brandLogoByName[brandName]} alt="" className="h-7 w-7 bg-white rounded p-0.5 object-contain" />
                    )}
                    <div className="font-semibold tracking-wider uppercase text-sm">{brandName}</div>
                    <div className="ml-auto text-xs opacity-80">{items.length} produit{items.length > 1 ? 's' : ''}</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {items.map((p) => <ProductCard key={p.id} p={p} {...opts} />)}
                </div>
            </div>
        ))}
    </div>
);

const PriceListPreview = ({ groupedByBrand, formatMoney }) => (
    <table className="w-full text-sm">
        <thead>
            <tr className="bg-[#1A1A1A] text-white text-xs uppercase tracking-wide">
                <th className="text-left py-2 px-3">SKU</th>
                <th className="text-left py-2 px-3">Produit</th>
                <th className="text-left py-2 px-3">Catégorie</th>
                <th className="text-right py-2 px-3">Prix</th>
            </tr>
        </thead>
        <tbody>
            {groupedByBrand.map(([brandName, items]) => (
                <>
                    <tr key={brandName} className="bg-[#1A1A1A]/90 text-white uppercase tracking-wide">
                        <td colSpan={4} className="px-3 py-2 text-xs font-semibold">{brandName} <span className="opacity-70 font-normal">({items.length})</span></td>
                    </tr>
                    {items.map((p) => (
                        <tr key={p.id} className="border-b border-gray-100 even:bg-[#FBF8F6]">
                            <td className="px-3 py-1.5 font-mono text-xs">{p.sku || '—'}</td>
                            <td className="px-3 py-1.5">{p.name}</td>
                            <td className="px-3 py-1.5 text-gray-500 text-xs">{p.category || ''}</td>
                            <td className="px-3 py-1.5 text-right font-bold text-[#C08478]">{formatMoney(p.price)}</td>
                        </tr>
                    ))}
                </>
            ))}
        </tbody>
    </table>
);

export default Catalog;
